import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { Avatar, Badge, Button } from "@/design-system";
import { Icon } from "@/components/Icon";
import { formatDays, formatDuration, formatTime, formatLongDate } from "@/lib/format";
import { isBookingsClosed, isBookingsNotOpen } from "@/lib/booking";
import { useOnline } from "@/lib/useOnline";
import { instructorLabel } from "@/lib/instructor";
import type { Lesson } from "@/lib/types";

/**
 * LessonDetailPane — the desktop master–detail right pane.
 *
 * A compact, in-context view of the selected lesson shown alongside
 * the schedule list on wide viewports, so booking doesn't require a
 * full-page navigation. On mobile the schedule still navigates to the
 * full <ClassDetailScreen> instead. The lesson object is already in
 * the list cache, so this renders without an extra fetch.
 */
export function LessonDetailPane({
  lesson,
  pending,
  onBook,
  onCancel,
}: {
  lesson: Lesson | null;
  pending: boolean;
  onBook: (lesson: Lesson) => void;
  onCancel: (lesson: Lesson) => void;
}) {
  const { t } = useTranslation();
  const online = useOnline();
  if (!lesson) {
    return (
      <div
        data-testid="lesson-detail-empty"
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-subtle)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-xs)",
          padding: "var(--space-10) var(--space-6)",
          textAlign: "center",
          color: "var(--text-faint)",
          font: "var(--fw-medium) var(--fs-sm)/1.5 var(--font-body)",
        }}
      >
        <Icon name="calendar" size={28} color="var(--text-faint)" />
        <div style={{ marginTop: 10 }}>{t("lessonPane.selectClass")}</div>
      </div>
    );
  }

  const isBooked = lesson.reserved_by_me;
  const isFull = lesson.spots_left <= 0 && !isBooked;
  const bookingsClosed = isBookingsClosed(lesson, new Date());
  const bookingsNotOpen = isBookingsNotOpen(lesson, new Date());

  return (
    <div
      data-testid="lesson-detail-pane"
      style={{
        background: "var(--surface-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-lg)",
        boxShadow: "var(--shadow-sm)",
        overflow: "hidden",
      }}
    >
      {/* hero */}
      <div
        style={{
          padding: "var(--space-5)",
          background: "var(--terracotta-700)",
          color: "#fff",
        }}
      >
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <Badge tone="brand" variant="solid">
            {formatTime(lesson.starts_at)}
          </Badge>
        </div>
        <div
          style={{
            font: "var(--fw-extra) var(--fs-2xl)/1.1 var(--font-display)",
            letterSpacing: "var(--ls-tight)",
          }}
        >
          {lesson.title}
        </div>
        <div
          style={{
            font: "var(--fw-regular) var(--fs-xs)/1.3 var(--font-body)",
            opacity: 0.9,
            marginTop: 6,
          }}
        >
          {formatLongDate(new Date(lesson.starts_at))} · {formatDuration(lesson.duration_minutes)}
        </div>
      </div>

      {/* body */}
      <div style={{ padding: "var(--space-5)", display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar name={instructorLabel(lesson)} size={40} ring />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                font: "var(--fw-bold) var(--fs-sm)/1.1 var(--font-display)",
                color: "var(--text-strong)",
              }}
            >
              {instructorLabel(lesson)}
            </div>
            <div
              style={{
                font: "var(--fw-regular) var(--fs-2xs)/1 var(--font-body)",
                color: "var(--text-faint)",
                marginTop: 3,
              }}
            >
              {t("lessonPane.spotsOfCapacity", {
                spotsLeft: lesson.spots_left,
                capacity: lesson.capacity,
              })}
            </div>
          </div>
        </div>

        <p
          style={{
            font: "var(--fw-regular) var(--fs-sm)/1.6 var(--font-body)",
            color: "var(--text-muted)",
            margin: 0,
          }}
        >
          {lesson.description?.trim() ||
            t("lessonPane.descriptionFallback", {
              duration: formatDuration(lesson.duration_minutes),
              capacity: lesson.capacity,
            })}
        </p>

        {isBooked ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Button
              variant="soft"
              size="md"
              fullWidth
              disabled={pending || bookingsClosed || !online}
              title={!online ? t("offline.bookingDisabled") : undefined}
              onClick={() => onCancel(lesson)}
              iconLeft={<Icon name="check" size={18} color="var(--brand-press)" />}
              data-testid="lesson-detail-cancel"
            >
              {bookingsClosed
                ? t("lessonPane.closed")
                : pending
                  ? t("common.cancelling")
                  : lesson.lesson_type === "assigned"
                    ? t("lessonPane.enrolledByTeacher")
                    : t("lessonPane.bookedCancel")}
            </Button>
            {bookingsClosed && (
              <span
                data-testid="lesson-detail-cancel-closed"
                style={{
                  font: "var(--fw-regular) var(--fs-2xs)/1.4 var(--font-body)",
                  color: "var(--text-faint)",
                  textAlign: "center",
                }}
              >
                {t("lessonPane.cancelClosedNote", {
                  duration: formatDuration(lesson.booking_cutoff_minutes),
                })}
              </span>
            )}
          </div>
        ) : lesson.lesson_type === "assigned" ? (
          <Button
            variant="secondary"
            size="md"
            fullWidth
            disabled
            data-testid="lesson-detail-assigned"
          >
            {t("lessonPane.assignedOnly")}
          </Button>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Button
              variant="primary"
              size="md"
              fullWidth
              disabled={pending || isFull || bookingsClosed || bookingsNotOpen || !online}
              title={!online ? t("offline.bookingDisabled") : undefined}
              onClick={() => onBook(lesson)}
              data-testid="lesson-detail-book"
            >
              {pending
                ? t("lessonPane.booking")
                : isFull
                  ? t("common.classFull")
                  : bookingsNotOpen
                    ? t("lessonPane.notOpen")
                    : bookingsClosed
                      ? t("lessonPane.closed")
                      : t("lessonPane.bookThisClass")}
            </Button>
            {isFull && (
              <span
                data-testid="lesson-detail-book-full"
                style={{
                  font: "var(--fw-regular) var(--fs-2xs)/1.4 var(--font-body)",
                  color: "var(--text-faint)",
                  textAlign: "center",
                }}
              >
                {t("common.waitlistSoon")}
              </span>
            )}
            {bookingsClosed && (
              <span
                data-testid="lesson-detail-book-closed"
                style={{
                  font: "var(--fw-regular) var(--fs-2xs)/1.4 var(--font-body)",
                  color: "var(--text-faint)",
                  textAlign: "center",
                }}
              >
                {t("lessonPane.closedNote", {
                  duration: formatDuration(lesson.booking_cutoff_minutes),
                })}
              </span>
            )}
            {bookingsNotOpen && (
              <span
                data-testid="lesson-detail-book-not-open"
                style={{
                  font: "var(--fw-regular) var(--fs-2xs)/1.4 var(--font-body)",
                  color: "var(--text-faint)",
                  textAlign: "center",
                }}
              >
                {t("lessonPane.notOpenNote", {
                  days: formatDays(lesson.booking_open_days ?? 0),
                })}
              </span>
            )}
          </div>
        )}

        <Link
          to={`/lessons/${lesson.id}`}
          style={{
            textAlign: "center",
            font: "var(--fw-semibold) var(--fs-xs)/1 var(--font-body)",
            color: "var(--brand)",
          }}
        >
          {t("lessonPane.openFullPage")}
        </Link>
      </div>
    </div>
  );
}
